import axios from 'axios';
import { getShortDateString } from '@/services/utils';
import type {
  ConflictCheckResponse,
  ConflictFieldId,
  ConflictMatchedField,
  ConflictPreviewClient,
  ConflictPreviewRecord,
  ConflictResultRow,
} from '../models/conflictTypes';
import {
  CONFLICT_BILLING_INSTRUCTIONS_FIELD,
  CONFLICT_CLIENT_FIELDS,
  CONFLICT_MATTER_FIELDS,
} from '../constants/conflictFieldConstants';

/**
 * The scan is measured at ~2.5s warm and ~5.3s cold (sc-47402), knowingly over the
 * 2s/4s target, so the default axios timeout is not enough to let a slow-but-working
 * check finish.
 */
const SCAN_TIMEOUT_MS = 45000;

/** A hung preview would otherwise leave the panel spinning with no way back. */
const PREVIEW_TIMEOUT_MS = 15000;

/** Matter conflict fields as the record endpoint returns them, keyed by the scan's field id. */
const MATTER_FIELD_SOURCES: Partial<
  Record<ConflictFieldId, (matter: MatterDetail) => string | null>
> = {
  matterName: (m) => m.name ?? m.label ?? null,
  matterNumber: (m) => m.reference ?? null,
  matterClients: (m) => m.custom?.relatedClients ?? null,
  matterAdverse: (m) => m.custom?.adverse ?? null,
  matterOpposingCounsel: (m) => m.custom?.opposingCounsel ?? null,
  matterRelated: (m) => m.custom?.relatedAdverse ?? null,
  matterReferralSource: (m) => m.custom?.referralSource ?? null,
  matterNotes: (m) => m.description ?? null,
  matterBillingInstructions: (m) => m.custom?.instructions ?? null,
};

/**
 * GET /clients/{id} carries the person's name parts under `contact`, not at the top
 * level, and does not return DisplayName at all — the scan reads that column but the
 * record endpoint has no field for it, so it is reported as unavailable rather than
 * empty. Raised with the API on the PR.
 */
const CLIENT_FIELD_SOURCES: Partial<
  Record<ConflictFieldId, (client: ClientDetail) => string | null>
> = {
  clientName: (c) => c.name ?? c.label ?? null,
  clientFirstName: (c) => c.contact?.first ?? null,
  clientLastName: (c) => c.contact?.last ?? null,
  clientDisplayName: () => null,
  clientCompanyName: (c) => c.contact?.companyName ?? null,
  clientEmail: (c) => c.contact?.email ?? null,
};

/** Searched by the scan, absent from the record endpoint. */
const CLIENT_FIELDS_NOT_RETURNED: ConflictFieldId[] = ['clientDisplayName'];

interface ClientDetail {
  id: string;
  label?: string;
  name?: string;
  reference?: string;
  contact?: {
    email?: string;
    phone?: string;
    first?: string;
    last?: string;
    companyName?: string;
    /** Sometimes a string, sometimes a nested object — both shapes are in the wild. */
    address?: string | Record<string, unknown>;
  };
}

/** The client's own box. The contact details are what a reviewer opened the row for. */
function clientBox(
  id: string | null,
  data: ClientDetail | null,
  archived: boolean,
): ConflictPreviewClient {
  const label = data?.name ?? data?.label ?? '';
  return {
    id,
    label,
    details: [
      { label: 'Name', value: label || null },
      { label: 'Client ID', value: data?.reference ?? null },
      { label: 'Status', value: archived ? 'Archived' : 'Active' },
      { label: 'Email', value: data?.contact?.email ?? null },
      { label: 'Phone', value: data?.contact?.phone ?? null },
      { label: 'Address', value: formatAddress(data?.contact?.address) },
    ],
  };
}

async function loadClient(id: string | null): Promise<ClientDetail | null> {
  if (!id) return null;
  try {
    const { data } = await axios.get<ClientDetail>(
      `/clients/${encodeURIComponent(id)}`,
      { timeout: PREVIEW_TIMEOUT_MS },
    );
    return data;
  } catch {
    // The client box is supporting detail; losing it must not lose the record.
    return null;
  }
}

/**
 * `opened` arrives as a full ISO timestamp; a reviewer wants the date. The house
 * helper owns the format, so this screen cannot drift from the rest of the app.
 */
function formatDate(value: string | undefined): string | null {
  if (!value) return null;
  return getShortDateString(value) || value;
}

/** Never render "[object Object]" or "{}" at a reviewer. */
function formatAddress(
  value: string | Record<string, unknown> | undefined,
): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value.trim() || null;
  const parts = Object.keys(value)
    .map((key) => value[key])
    .filter(
      (part): part is string => typeof part === 'string' && part.trim() !== '',
    );
  return parts.length ? parts.join(', ') : null;
}

interface MatterDetail {
  id: string;
  label?: string;
  name?: string;
  reference?: string;
  description?: string;
  /** The matter endpoint reports `active`; there is no `archived` field. */
  active?: boolean;
  opened?: string;
  client?: { id?: string; label?: string };
  custom?: {
    adverse?: string;
    relatedAdverse?: string;
    relatedClients?: string;
    opposingCounsel?: string;
    referralSource?: string;
    instructions?: string;
    practiceArea?: string;
  };
}

interface EntryDetail {
  id: string;
  date?: string;
  description?: string;
  hours?: number;
  account?: { name?: string; initials?: string };
  matter?: {
    id?: string;
    label?: string;
    client?: { id?: string; label?: string };
  };
}

/** Matched words for one field of one record, so the panel highlights what the row did. */
function matchedWordsFor(
  rows: ConflictResultRow[],
  field: ConflictFieldId,
): string[] {
  const seen: Record<string, true> = {};
  const words: string[] = [];
  rows
    .reduce(
      (all: ConflictMatchedField[], row) => all.concat(row.fields),
      [],
    )
    .filter((matched) => matched.id === field)
    .forEach((matched) => {
      matched.matchedWords.forEach((word) => {
        if (!seen[word]) {
          seen[word] = true;
          words.push(word);
        }
      });
    });
  return words;
}

export default {
  async runCheck(
    query: string,
    includeTimeEntries: boolean,
  ): Promise<ConflictCheckResponse> {
    const { data } = await axios.get<ConflictCheckResponse>(
      '/search/conflict',
      {
        params: { query, entries: includeTimeEntries },
        timeout: SCAN_TIMEOUT_MS,
      },
    );
    return data;
  },

  async fetchClientPreview(
    id: string,
    rows: ConflictResultRow[],
  ): Promise<ConflictPreviewRecord> {
    const { data } = await axios.get<ClientDetail>(
      `/clients/${encodeURIComponent(id)}`,
      { timeout: PREVIEW_TIMEOUT_MS },
    );
    // The client endpoint has no archived flag; the scan row does, so it is the source.
    const archived = rows.some((row) => row.archived);
    return {
      id,
      type: 'client',
      label: data.name ?? data.label ?? '',
      archived,
      // No separate client box here — this record is the client.
      summary: clientBox(id, data, archived).details,
      searchedFields: CONFLICT_CLIENT_FIELDS.map((field) => ({
        id: field,
        value: CLIENT_FIELD_SOURCES[field]?.(data) ?? null,
        matchedWords: matchedWordsFor(rows, field),
        unavailable: CLIENT_FIELDS_NOT_RETURNED.indexOf(field) !== -1,
      })),
    };
  },

  async fetchMatterPreview(
    id: string,
    rows: ConflictResultRow[],
    canSeeBillingInstructions: boolean,
  ): Promise<ConflictPreviewRecord> {
    const { data } = await axios.get<MatterDetail>(
      `/matters/${encodeURIComponent(id)}`,
      {
        timeout: PREVIEW_TIMEOUT_MS,
      },
    );
    // Billing instructions are permission-gated everywhere else in the product; the
    // preview must not be the one place they leak.
    const fields = canSeeBillingInstructions
      ? CONFLICT_MATTER_FIELDS
      : CONFLICT_MATTER_FIELDS.filter((f) => f !== CONFLICT_BILLING_INSTRUCTIONS_FIELD);
    const archived = data.active === false;
    const clientId = data.client?.id ?? null;
    const client = await loadClient(clientId);
    const name = data.name ?? data.label ?? '';
    return {
      id,
      type: 'matter',
      label: name,
      archived,
      summary: [
        { label: 'Name', value: name || null },
        { label: 'Matter number', value: data.reference ?? null },
        { label: 'Status', value: archived ? 'Archived' : 'Open' },
        { label: 'Opened', value: formatDate(data.opened) },
        { label: 'Practice area', value: data.custom?.practiceArea ?? null },
      ],
      client: {
        ...clientBox(clientId, client, false),
        label: client?.name ?? data.client?.label ?? '',
      },
      searchedFields: fields.map((field) => ({
        id: field,
        value: MATTER_FIELD_SOURCES[field]?.(data) ?? null,
        matchedWords: matchedWordsFor(rows, field),
      })),
    };
  },

  async fetchEntryPreview(
    id: string,
    rows: ConflictResultRow[],
  ): Promise<ConflictPreviewRecord> {
    const { data } = await axios.get<EntryDetail>(
      `/entries/${encodeURIComponent(id)}`,
      {
        timeout: PREVIEW_TIMEOUT_MS,
      },
    );
    const clientId = data.matter?.client?.id ?? null;
    const client = await loadClient(clientId);
    return {
      id,
      type: 'timeEntry',
      label: data.matter?.label ?? '',
      archived: false,
      summary: [
        { label: 'Matter', value: data.matter?.label ?? null },
        { label: 'Date', value: formatDate(data.date) },
        { label: 'User', value: data.account?.name ?? null },
        {
          label: 'Hours',
          value: data.hours != null ? String(data.hours) : null,
        },
      ],
      client: {
        ...clientBox(clientId, client, false),
        label: client?.name ?? data.matter?.client?.label ?? '',
      },
      searchedFields: [
        {
          id: 'timeEntryDescription',
          value: data.description ?? null,
          matchedWords: matchedWordsFor(rows, 'timeEntryDescription'),
        },
      ],
    };
  },
};
